import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/pro-regular-svg-icons";
import { useForm } from "react-hook-form";

import { T, useT } from "@repo/transifex";
import { useUserProductsPos } from "~/api/products/hooks/use-user-products";
import { useOrderEmail } from "~/hooks/queries/orders";

import { Button } from "..";
import { useToast } from "../hooks/use-toast";

type EmailDialogProps = {
  orderId: string;
  onClose: () => void;
};

export const EmailDialog = ({ orderId, onClose }: EmailDialogProps) => {
  const t = useT();
  const { toast } = useToast();
  const { mutate: sendEmail, status } = useOrderEmail();
  const { data: userProducts } = useUserProductsPos();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<{
    email: string;
  }>();

  return (
    <div className="flex flex-col items-center justify-center px-8 py-5 text-center">
      <div className="mb-8 flex flex-col gap-3 text-text-secondary">
        <h2 className="text-xl">
          <T _str="Send receipt to email" />
        </h2>
        <p className="text-xs">
          <T _str="Enter email" />
        </p>
      </div>
      <form
        className="flex w-full flex-col items-center justify-center"
        onSubmit={handleSubmit(({ email }) => {
          if (!orderId) return;
          sendEmail(
            {
              orderId,
              email,
              userId: userProducts?.user?.id ?? null
            },
            {
              onSuccess: () => {
                toast({
                  title: t("Email sent!"),
                  variant: "success"
                });
                onClose();
              },
              onError: () => {
                toast({
                  title: t("Error sending the email, try again"),
                  variant: "destructive"
                });
              }
            }
          );
        })}
      >
        <div className="relative flex w-full max-w-[70%] flex-col rounded-md border border-gray-300 px-3 py-2 text-left">
          <label className="text-xs text-text-disabled">
            <T _str="Email" />
          </label>
          <input
            type="email"
            disabled={status === "pending"}
            className="border-none text-sm text-text-secondary outline-none"
            {...register("email", {
              required: t("Email is required"),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t("Invalid email address")
              }
            })}
          />
          <FontAwesomeIcon
            className="absolute right-3 top-1/3"
            icon={faEnvelope}
          />
        </div>
        {errors.email ? (
          <div className="text-xs text-red-600">{errors.email.message}</div>
        ) : null}

        <div className="mt-6 flex gap-3">
          <Button onClick={onClose} variant="danger-light">
            <T _str="Cancel" />
          </Button>
          <Button
            type="submit"
            loading={status === "pending"}
            variant="primary"
          >
            <T _str="Send" />
          </Button>
        </div>
      </form>
    </div>
  );
};
